import { lazy, Suspense, type ComponentType, type ReactElement } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { BrandedLoadingState } from './components/BrandedLoadingState';
import { AppShell } from './components/layout/AppShell';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { ProfileGate } from './components/layout/ProfileGate';
import { RouteErrorFallback } from './components/RouteErrorFallback';
import { AdminRoute } from './admin/components/AdminRoute';
import { LandingPage } from './pages/LandingPage';
import { AboutPage } from './pages/public/AboutPage';
import { FeaturesPage } from './pages/public/FeaturesPage';
import { ForRidersPage } from './pages/public/ForRidersPage';
import { ForRidingGroupsPage } from './pages/public/ForRidingGroupsPage';
import { HowItWorksPage } from './pages/public/HowItWorksPage';
import { PrivacyPage } from './pages/public/PrivacyPage';
import { TermsPage } from './pages/public/TermsPage';

// Route-level code splitting: each page ships in its own chunk, loaded on demand.
const named = <M extends Record<string, ComponentType>>(loader: () => Promise<M>, key: keyof M) =>
  lazy(() => loader().then((m) => ({ default: m[key] })));

const LoginPage = named(() => import('./pages/LoginPage'), 'LoginPage');
const AdminLoginPage = named(() => import('./admin/pages/AdminLoginPage'), 'AdminLoginPage');
const AdminConsolePage = named(() => import('./admin/pages/AdminConsolePage'), 'AdminConsolePage');
const JoinRidePage = named(() => import('./pages/JoinRidePage'), 'JoinRidePage');
const DashboardPage = named(() => import('./pages/DashboardPage'), 'DashboardPage');
const ProfileSetupPage = named(() => import('./pages/ProfileSetupPage'), 'ProfileSetupPage');
const ProfilePage = named(() => import('./pages/ProfilePage'), 'ProfilePage');
const SettingsPage = named(() => import('./pages/SettingsPage'), 'SettingsPage');
const ExplorePage = named(() => import('./pages/ExplorePage'), 'ExplorePage');
const CreateRidePage = named(() => import('./pages/CreateRidePage'), 'CreateRidePage');
const RideDetailsPage = named(() => import('./pages/RideDetailsPage'), 'RideDetailsPage');
const EditRidePage = named(() => import('./pages/EditRidePage'), 'EditRidePage');
const CaptainDashboardPage = named(() => import('./pages/CaptainDashboardPage'), 'CaptainDashboardPage');
const NotificationsPage = named(() => import('./pages/NotificationsPage'), 'NotificationsPage');
const RecentActivityPage = named(() => import('./pages/RecentActivityPage'), 'RecentActivityPage');
const NotFoundPage = named(() => import('./pages/NotFoundPage'), 'NotFoundPage');

/** Wrap a lazy page in a Suspense boundary with a consistent loading state. */
function page(node: ReactElement): ReactElement {
  return <Suspense fallback={<BrandedLoadingState label="Loading page…" />}>{node}</Suspense>;
}

export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorFallback />,
    children: [
      { path: '/', element: <LandingPage /> },
      { path: '/features', element: <FeaturesPage /> },
      { path: '/how-it-works', element: <HowItWorksPage /> },
      { path: '/for-riders', element: <ForRidersPage /> },
      { path: '/for-riding-groups', element: <ForRidingGroupsPage /> },
      { path: '/about', element: <AboutPage /> },
      { path: '/privacy', element: <PrivacyPage /> },
      { path: '/terms', element: <TermsPage /> },
      { path: '/login', element: page(<LoginPage />) },
      { path: '/admin/login', element: page(<AdminLoginPage />) },
      {
        path: '/admin',
        element: (
          <AdminRoute>{page(<AdminConsolePage />)}</AdminRoute>
        ),
      },
      { path: '/j/:slug', element: page(<JoinRidePage />) },
      {
        path: '/profile/setup',
        element: (
          <ProtectedRoute>{page(<ProfileSetupPage />)}</ProtectedRoute>
        ),
      },
      {
        element: (
          <ProtectedRoute>
            <ProfileGate>
              <AppShell />
            </ProfileGate>
          </ProtectedRoute>
        ),
        children: [
          { path: '/dashboard', element: page(<DashboardPage />) },
          { path: '/explore', element: page(<ExplorePage />) },
          { path: '/rides/new', element: page(<CreateRidePage />) },
          { path: '/rides/:id', element: page(<RideDetailsPage />) },
          { path: '/rides/:id/edit', element: page(<EditRidePage />) },
          { path: '/rides/:id/captain', element: page(<CaptainDashboardPage />) },
          { path: '/notifications', element: page(<NotificationsPage />) },
          { path: '/activity', element: page(<RecentActivityPage />) },
          { path: '/profile', element: page(<ProfilePage />) },
          { path: '/settings', element: page(<SettingsPage />) },
        ],
      },
      { path: '/404', element: page(<NotFoundPage />) },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
]);
